import { excludeNullOrUndefined, getBreakdownTitle } from 'checkout/utils'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { isCruiseItem, isTourV1Item } from 'lib/checkout/checkoutUtils'
import createSelector from 'lib/web/createSelector'
import { pluralizeToString } from 'lib/string/pluralize'
import moment from 'moment'

import { getLocationString } from 'checkout/lib/utils/accommodation/location'
import { getDepositServiceFeeConfig } from 'checkout/selectors/featureConfig/deposit'
import { DepositType } from 'constants/checkout'
import { buildCruiseItemView } from 'checkout/lib/utils/cruises/view'
import { buildCruiseSummaryBreakdown } from 'checkout/lib/utils/cruises/summary'
import { getCheckoutRouteFromURL, getIsCabinRateStep } from 'checkout/lib/utils/cruises/steps'
import { getCruiseCountriesByItinerary, getCruisePortsByItinerary, getCruiseDurationByItinerary } from 'lib/cruises/cruiseUtils'
import { sum, EmptyArray, arrayToObject, sortBy, groupBy } from 'lib/array/arrayUtils'

export const getCruiseItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.CruiseItem> => items.filter(isCruiseItem),
)

/** SOME RATES HAVE A WAITING LIST CATEGORY AND
 * WE ONLY KNOW WHEN "/CABIN-AVAILABILITY" IS REQUESTED

 * WE NEED TO SAVE THOSE CATEGORIES IN THE STATE AND FILTER
 * THE DATA SO AS TO NOT DISPLAY THE SAME UNAVAILABLE RATE **/
export const getCabinRates = createSelector(
  (state: App.State) => state.cruise.cabinRates,
  (state: App.State) => state.cruise.waitListedCategories,
  (cabinRates, waitListedCategories): Array<App.CruiseCabinRateView> => {
    return cabinRates?.filter((item) =>
      !waitListedCategories.includes(item.componentId)).filter(Boolean) ?? EmptyArray
  },
)

export const getBookingCabinRates = createSelector(
  (state: App.State) => state.cruise.booking.cabinRates,
  (state: App.State) => state.cruise.waitListedCategories,
  (cabinRates, waitListedCategories): Array<Cruises.BookingCabinRate> => {
    return cabinRates?.filter((item) =>
      !waitListedCategories.includes(item.componentId)).filter(Boolean) ?? EmptyArray
  },
)

export function getCruiseBreakdownView(
  offerView: App.Checkout.CruiseAccommodationOfferView,
  hidePrice: boolean,
): App.Checkout.PriceBreakdownView {
  const { startDate, endDate, saleUnit, itemViews, mainLabel } = offerView

  const cruiseDuration = getCruiseDurationByItinerary(offerView.offer?.itinerary ?? [])

  const items = itemViews.map<App.Checkout.ItemBreakdownView>(
    (itemView: App.Checkout.CruiseAccommodationItemView, index) => {
      const { additionalElements, additionalInfoText } = buildCruiseSummaryBreakdown(itemView)

      const onboardCreditsV1 = itemView.cabinRateView?.onboardCredits || itemView.cabinHold?.pricing?.onboardCredits
      const onboardCreditsV2 = itemView.cabinSelection?.pricing?.onboardCredit

      const onboardCreditAmount = onboardCreditsV1?.amount || onboardCreditsV2?.amount
      const onboardCreditCurrencyCode = onboardCreditsV1?.currencyCode || onboardCreditsV2?.currency

      const hasOnboardCredits = !!(onboardCreditAmount && onboardCreditCurrencyCode)

      const price = (hidePrice || !window.location.pathname.includes('purchase')) ? undefined : (itemView?.price ?? 0) - itemView.taxesAndFees
      const title = [`${saleUnit} ${index + 1}`, itemView.cabinCategory?.cabinType].filter(Boolean).join(' - ')

      return {
        title,
        additionalInfoText,
        cancellationPolicy: itemView.cabinHold?.pricing.cancellationPolicy,
        additionalElements,
        price,
        taxesAndFees: itemView.taxesAndFees,
        offerId: itemView.item.offerId,
        itemType: 'cruise',
        ...(hasOnboardCredits && {
          onboardCredits: {
            amount: onboardCreditAmount,
            currencyCode: onboardCreditCurrencyCode,
          },
        }),
      }
    })

  const formattedStartDate = startDate ? `${moment(startDate).format(DMY_CASUAL_FORMAT)}` : 'start'
  const formattedEndDate = endDate ? `${moment(endDate).format(DMY_CASUAL_FORMAT)}` : 'end'
  const price = sum(items, item => (item.price ?? 0))
  const memberPrice = sum(items, item => (item.memberPrice ?? 0))

  return {
    title: getBreakdownTitle(offerView),
    description: mainLabel,
    additionalInfoText: [
      `${offerView.offer?.cruiseLine.name} - ${offerView.offer?.ship.name}`,
      `${formattedStartDate} - ${formattedEndDate} - ${pluralizeToString('night', cruiseDuration - 1)}`,
    ],
    items,
    price,
    memberPrice,
    onboardCredits: items.find(item => item.onboardCredits)?.onboardCredits,
  }
}

export const getCheckoutCruiseOfferView = createSelector(
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => state.cruise.cruiseOffers,
  (state: App.State) => state.cruise.cabinRates,
  (state: App.State) => state.cruise.cabinRateDetails,
  (state: App.State) => state.cruise.cruiseDeparture,
  (state: App.State) => state.cruise.cabinAvailability,
  (state: App.State) => state.cruise.mapLegend,
  (state: App.State) => state.cruise.deckSelections,
  (state: App.State) => state.cruise.cabinHold,
  getDepositServiceFeeConfig,
  (state: App.State) => state.cruise.cabinPricing,
  (state: App.State) => state.cruise.booking.cabinSelection,
  (state: App.State) => state.cruise.booking.cabinRates,
  (state: App.State) => state.cruise.booking.rateDetailsList,
  (state: App.State) => state.router.location.pathname,
  (
    items,
    offers,
    rates,
    rateDetails,
    departures,
    availableCabinList,
    legendList,
    deckSelections,
    cabinHold,
    serviceFee,
    cabinPricing,
    cabinSelection,
    cabinRates,
    rateDetailsList,
    pathname,
  ): App.WithDataStatus<Array<App.Checkout.CruiseAccommodationOfferView>> => {
    if (items.length === 0) { return { hasRequiredData: true, data: [] } }

    const item = items[0]
    const offer = offers[item.offerId]

    const dayDuration = getCruiseDurationByItinerary(offer?.itinerary)
    const countriesCount = getCruiseCountriesByItinerary(offer?.itinerary)
    const portsCount = getCruisePortsByItinerary(offer?.itinerary)

    const cruiseCheckoutStep = getCheckoutRouteFromURL(pathname)
    const isCabinRateStep = getIsCabinRateStep(cruiseCheckoutStep)

    let hasRequiredData = false
    const offerView: App.Checkout.CruiseAccommodationOfferView = {
      offer,
      offerId: item.offerId,
      mainLabel: offer?.name,
      duration: offer?.duration,
      startDate: departures?.[item.departureId]?.departureDate,
      endDate: departures?.[item.departureId]?.returnDate,
      image: offer?.images?.[0],
      location: getLocationString(offer),
      occupancy: items.map(item => item.occupancy).filter(excludeNullOrUndefined),
      offerType: offer?.type,
      reservationType: 'instant_booking',
      saleUnit: 'cabin',
      durationLabel: pluralizeToString('night', dayDuration - 1),
      offerLoaded: !!offer,
      itemViews: [],
      propertyTimezone: 'GMT',
      operatorName: offer ? `Operated by: ${offer.cruiseLine.name}` : '',
      shipName: offer?.ship ? `Cruise ship: ${offer.ship.name}` : '',
      countries: countriesCount,
      ports: portsCount,
      serviceFee,
      depositType: DepositType.FLAT,
      designation: 'Cruise',
      ship: offer?.ship,
      availableCabinList: availableCabinList ?? [],
      legendList,
      deckSelections: deckSelections ?? {},
      urgencyLabels: [],
      bedGroups: [],
      confidenceLabels: [],
    }

    if (item.version === 'v2') {
      const depositAmount = cabinSelection?.pricing.paymentSchedule?.firstPayment?.amount ?? null
      const rate = cabinRates ? cabinRates.find(rate => rate.componentId === item.componentId) : undefined
      // const details = rateDetailsList?.[item.componentId || '']
      const details = rateDetails?.[item.componentId || '']
      const itemViewWithStatus = buildCruiseItemView(item, offer, rate, undefined, details, cabinPricing, cabinSelection)

      // The data required is different according to the checkout stage
      hasRequiredData = isCabinRateStep ? !!offer : [
        itemViewWithStatus.hasRequiredData,
        !!offer,
        !!cabinSelection,
      ].every(Boolean)

      return {
        hasRequiredData,
        data: [{
          ...offerView,
          itemViews: [itemViewWithStatus.data],
          ...(depositAmount && { depositAmount }),
        }],
      }
    }

    const rate = rates ? rates.find(rate => rate.componentId === item.componentId) : undefined
    const depositAmount = cabinPricing?.paymentScheduleDetails?.schedule?.firstPayment?.amount ?? null

    const details = rateDetails?.[item.componentId || '']
    const itemViewWithStatus = buildCruiseItemView(item, offer, rate, cabinHold, details, cabinPricing)

    // The data required is different according to the checkout stage
    hasRequiredData = isCabinRateStep ? !!offer : [
      itemViewWithStatus.hasRequiredData,
      !!availableCabinList && availableCabinList.length > 0,
      !!offer,
      !!rate,
    ].every(Boolean)

    return {
      hasRequiredData,
      data: [{
        ...offerView,
        itemViews: [itemViewWithStatus.data],
        ...(depositAmount && { depositAmount }),
      }],
    }
  },
)

export const getCheckoutCruiseOffer = createSelector(
  getCheckoutCruiseOfferView,
  (offerViewWithStatus): App.CruiseOffer | undefined => offerViewWithStatus.data[0]?.offer,
)

export const getCruisesV1 = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (items, leOffers) => {
    return items.filter(isTourV1Item).filter(item => {
      const offer = leOffers[item.offerId]
      return !!offer && !!offer.holidayTypes?.includes('Cruises')
    })
  },
)

export const isCruiseCheckout = createSelector(
  getCruiseItems,
  getCruisesV1,
  (state: App.State) => state.checkout.cart.existingOrder?.cruiseItems,
  (cruiseItems, cruiseOfTourV1Items, existingCruiseItems) => {
    return !!cruiseItems.length || !!cruiseOfTourV1Items.length || !!existingCruiseItems?.length
  },
)

// BOOKING V2
type DeckAvailability = {
  [deckId: string]: App.CruiseDeck
}
export const getDecksAvailable = createSelector(
  (state: App.State) => getCheckoutCruiseOffer(state),
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => state.cruise.booking.cabinList,
  (offer, cruiseItems, cabinList): Array<App.CruiseDeck> => {
    const [cruiseItem] = cruiseItems
    if (!offer || !cabinList || !cruiseItem) return []

    const shipDeckMap = arrayToObject(offer.ship.decks, (shipDeck) => shipDeck.externalId)

    const availability = Object.values(cabinList ?? {}).reduce((acc: DeckAvailability, cabin: Cruises.BookingCabin): DeckAvailability => {
      const deckId = cabin.deckId as string
      const availability = acc[deckId]

      let newAvailability: App.CruiseDeck | null = null

      if (availability) {
        newAvailability = {
          ...availability,
          cabinAmount: availability.cabinAmount + 1,
        }
      } else {
        const shipDeck = shipDeckMap[deckId]
        newAvailability = {
          deckId,
          deckName: cabin.deckName || 'Deck',
          deckNumber: shipDeck?.deckNumber,
          image: shipDeck?.imageId ?? '',
          shipCabinPositionList: shipDeck?.shipCabinPosition ?? [],
          cabinAmount: 1,
        }
      }

      return { ...acc, [deckId]: newAvailability }
    }, {})

    return sortBy(Object.values(availability), deck => deck.deckNumber)
  },
)

export const getCabinsWithRatesByCategory = createSelector(
  (state: App.State) => state.cruise.booking.cabinRates,
  (state: App.State) => state.cruise.booking.cabinList,
  (state: App.State) => getCruiseItems(state),
  (cabinRates, cabinList, cruiseItems): Array<Cruises.BookingCabinsWithRatesByCategory> => {
    const cruiseItem = cruiseItems[0]
    if (cabinRates && cabinList && cruiseItem) {
      const selectedTypeRates = cabinRates.filter(rate => rate.cabinType === cruiseItem.cabinType)
      const ratesByCategory = groupBy(selectedTypeRates, rate => rate.pricedCategoryCode)

      return [...ratesByCategory.entries()].map(([category, categoryRates]) => {
        const componentIds = categoryRates.map(rate => rate.componentId)
        const cabinsWithRates = Object.values(cabinList ?? {})
          .filter(cabin => Number(cabin.deckId) === cruiseItem.deckId && componentIds.some(id => cabin.componentIds.includes(id)))
          .map((cabin) => {
            const rates = categoryRates.filter(rate => cabin.componentIds.includes(rate.componentId))
            return { ...cabin, rates }
          }) as Array<Cruises.BookingCabinWithRates>

        return {
          category,
          cabins: cabinsWithRates,
        }
      }).filter(({ cabins }) => cabins.length > 0)
    }

    return []
  },
)
